import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { Me } from '@wo/shared';
import { api, errorText } from '../../lib/api';
import { dateTime } from '../../lib/format';
import { Button, Card, ErrorText, Loading, PageHeader } from '../../components/ui';

interface Pending {
  id: number;
  requestedBy: number;
  reason: string;
  createdAt: string;
  workOrder: { id: number; number: number; customer: { name: string } };
}

export function VoidApprovalsPage({ me }: { me: Me }) {
  const qc = useQueryClient();
  const rows = useQuery({ queryKey: ['voids'], queryFn: () => api.get<Pending[]>('/api/void-requests') });
  const [error, setError] = useState<string | null>(null);
  const decide = async (id: number, approve: boolean) => {
    setError(null);
    try {
      await api.post(`/api/void-requests/${id}/decision`, { approve });
      qc.invalidateQueries({ queryKey: ['voids'] });
      qc.invalidateQueries({ queryKey: ['board'] });
    } catch (e) {
      setError(errorText(e));
    }
  };
  if (rows.isLoading) return <Loading />;
  return (
    <>
      <PageHeader title="Void Approvals" />
      <ErrorText error={error} />
      <Card>
        {rows.data?.length === 0 && <p className="text-sm text-slate-500">No void requests waiting.</p>}
        <ul className="divide-y">
          {rows.data?.map((v) => (
            <li key={v.id} className="flex flex-wrap items-center justify-between gap-2 py-3">
              <div>
                <Link className="font-bold text-blue-800 hover:underline" to={`/work-orders/${v.workOrder.id}`}>#{v.workOrder.number}</Link>{' '}
                {v.workOrder.customer.name}
                <div className="text-sm">“{v.reason}” · {dateTime(v.createdAt)}</div>
              </div>
              {v.requestedBy === me.id ? (
                <span className="text-sm text-slate-500">Your request — someone else must approve</span>
              ) : (
                <div className="flex gap-2">
                  <Button variant="danger" onClick={() => decide(v.id, true)}>Approve</Button>
                  <Button variant="secondary" onClick={() => decide(v.id, false)}>Deny</Button>
                </div>
              )}
            </li>
          ))}
        </ul>
      </Card>
    </>
  );
}
